// First-party VOD upload service: asset creation + the multipart upload
// session control-plane calls (open / part URLs / complete / poll / abort).

import { api, buildUrl, getAuthToken } from './api'
import type {
  CreateVodAssetRequest,
  OpenVodUploadResponse,
  VodAsset,
  VodUploadCompleteResult,
  VodUploadPartUrl,
  VodUploadPollResponse,
  VodUploadedPart,
} from '../types/vod'

/**
 * Create a first-party VOD asset (metadata first, HIDDEN, no media)
 */
export const createVodAsset = (body: CreateVodAssetRequest): Promise<{ asset: VodAsset }> => {
  return api.post('/vod/assets', body)
}

/**
 * Open a multipart upload session against the asset's reserved master key.
 * 409 (virtual asset, or a session already in flight) surfaces as
 * ConflictError; 412 (the page's master version is stale) as
 * PreconditionFailedError.
 */
export const openVodUpload = (
  assetId: string,
  body: {
    filename: string
    content_type: string
    size_bytes: number
    expected_master_version: number
  }
): Promise<OpenVodUploadResponse> => {
  return api.post(`/vod/assets/${assetId}/media/uploads`, body)
}

/**
 * Presign a batch of part-upload URLs (URLs expire ~1h; re-requesting is always legal)
 */
export const fetchVodUploadPartUrls = (
  assetId: string,
  uploadId: string,
  partNumbers: number[]
): Promise<{ parts: VodUploadPartUrl[] }> => {
  return api.get(`/vod/assets/${assetId}/media/uploads/${uploadId}/parts`, {
    part_numbers: partNumbers,
  })
}

/**
 * Poll the session: state display + resume (uploadedParts while UPLOADING,
 * assembled: true once the MPU was consumed). The poll's one write is the
 * session's activity heartbeat.
 */
export const pollVodUpload = (
  assetId: string,
  uploadId: string
): Promise<VodUploadPollResponse> => {
  return api.get(`/vod/assets/${assetId}/media/uploads/${uploadId}`)
}

/**
 * Abort an in-flight session (the burned version is never reused)
 */
export const abortVodUpload = (
  assetId: string,
  uploadId: string
): Promise<{ state: 'ABORTED' }> => {
  return api.delete(`/vod/assets/${assetId}/media/uploads/${uploadId}`)
}

/**
 * Short-lived presigned GET of the asset's SOURCE master for editor preview
 * (15 min TTL; 404 when no master is catalogued). Not a delivery URL.
 */
export const fetchVodMasterPreview = (
  assetId: string
): Promise<{ url: string; expires_in: number }> => {
  return api.get(`/vod/assets/${assetId}/media/preview`)
}

/**
 * Finalize: assemble → validate → catalog, synchronous end to end.
 *
 * Unlike the shared request helper this keeps the response body on every
 * contract status — the endpoint speaks through them:
 *   200 COMPLETED · 422 FAILED (failure_code) · 503 transient (retry heals)
 *   · 409 another finalize in flight (or ABORTED replay)
 * Pass parts for the first assembly; an empty body re-validates an already
 * assembled object (retry after reload / stale VALIDATING).
 */
// Past the ALB's 60s idle timeout, so the server-side limit binds first and a
// hung request can never wedge the client's retry loop
const COMPLETE_TIMEOUT_MS = 90_000

export const completeVodUpload = async (
  assetId: string,
  uploadId: string,
  parts?: VodUploadedPart[]
): Promise<VodUploadCompleteResult> => {
  const url = buildUrl(`/vod/assets/${assetId}/media/uploads/${uploadId}/complete`)
  const token = await getAuthToken()

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), COMPLETE_TIMEOUT_MS)

  let response
  try {
    response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(parts && parts.length > 0 ? { parts } : {}),
      signal: controller.signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw new Error('Finalize request timed out — it will be retried')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }

  const body = await response.json().catch(() => ({}))

  switch (response.status) {
    case 200:
      return {
        status: 'completed',
        session: body.session,
        warnings: body.warnings ?? body.session?.warnings ?? [],
        asset: body.asset,
      }
    case 422:
      return { status: 'failed', session: body.session }
    case 503:
      return { status: 'transient', session: body.session, detail: body.detail }
    case 409:
      return { status: 'in_progress', session: body.session, detail: body.detail }
    default:
      throw new Error(
        typeof body.detail === 'string'
          ? body.detail
          : `Finalize failed (HTTP ${response.status})`
      )
  }
}
